import { Reveal, Eyebrow } from "./Reveal";
import { ComplianceBadge } from "@/components/ds";
import { GROUP_I, GROUP_II } from "@/data/commodities";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const BADGES = [
  { label: "FATF Aligned", detail: "Recommendations 10–21 procedures" },
  { label: "OECD DDG", detail: "Responsible mineral supply chains" },
  { label: "KYC / AML Verified", detail: "Structured clearance before engagement" },
  { label: "Sanctions Screened", detail: "OFAC · EU · UN · UK HMT" },
  { label: "Dodd-Frank 1502", detail: "Conflict-mineral due diligence" },
  { label: "Chain of Custody", detail: "Mine gate to final delivery" },
];

const ImageTile = ({ c }) => (
  <Link
    to={`/commodities/${c.slug}`}
    data-testid={`commodity-card-${c.slug}`}
    className="group bg-[#0A0A0B] hover:bg-[#16161A] transition-colors block h-full"
  >
    <div className="relative overflow-hidden">
      <img
        src={c.img}
        alt={`${c.name} — ${c.spec}`}
        loading="lazy"
        className="w-full h-56 object-cover group-hover:scale-[1.03] transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
      {c.tag && (
        <span className="absolute top-4 left-4 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#E8C85A] bg-[#0A0A0B]/85 backdrop-blur px-3 py-1 card-line">
          {c.tag}
        </span>
      )}
    </div>
    <div className="p-8">
      <h3 className="font-display text-xl text-white tracking-wide group-hover:text-[#E8C85A] transition-colors">{c.name}</h3>
      <div className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#C9A227]">{c.spec}</div>
      <div className="mt-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">{c.origin}</div>
      <span className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] group-hover:text-[#E8C85A] transition-colors">
        View assay & details <ArrowUpRight size={14} />
      </span>
    </div>
  </Link>
);

const CompactTile = ({ c }) => (
  <Link
    to={`/commodities/${c.slug}`}
    data-testid={`commodity-tile-${c.slug}`}
    className="group bg-[#0A0A0B] p-7 hover:bg-[#16161A] transition-colors block h-full"
  >
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-display text-lg text-white tracking-wide group-hover:text-[#E8C85A] transition-colors">{c.name}</h3>
      <ArrowUpRight size={16} className="text-zinc-600 group-hover:text-[#D4AF37] transition-colors shrink-0 mt-1" />
    </div>
    <div className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#C9A227]">{c.spec}</div>
    <div className="mt-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">{c.origin}</div>
  </Link>
);

export const Commodities = () => (
  <section id="commodities" data-testid="commodities-section" className="py-28 lg:py-36 bg-[#121215] hairline-t hairline-b">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="max-w-3xl">
        <Eyebrow>Compliance Framework</Eyebrow>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="compliance-badges">
          {BADGES.map((b) => (
            <ComplianceBadge key={b.label} {...b} testid={`badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
          ))}
        </div>
      </Reveal>

      <Reveal className="max-w-2xl mt-24">
        <Eyebrow>Commodity Portfolio</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white leading-snug">
          Two groups. <span className="gold-text">One standard.</span>
        </h2>
        <p className="mt-6 text-base text-zinc-400 leading-relaxed">
          Group I — precious minerals and strategic resources. Group II —
          industrial minerals from our East and Central African supply corridors.
          Every lot assayed, every source verified, every number on the record.
        </p>
      </Reveal>

      <h3 className="mt-14 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#C9A227]" data-testid="group-i-heading">
        Group I — Precious Minerals & Strategic Resources
      </h3>
      <div className="mt-6 grid md:grid-cols-2 gap-px bg-[#C9A227]/15 card-line">
        {GROUP_I.map((c) => <ImageTile key={c.slug} c={c} />)}
      </div>

      <h3 className="mt-16 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#C9A227]" data-testid="group-ii-heading">
        Group II — Industrial & Critical Minerals
      </h3>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C9A227]/15 card-line">
        {GROUP_II.map((c) => <CompactTile key={c.slug} c={c} />)}
      </div>
    </div>
  </section>
);
